import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { connect } from "react-redux";
import { useModals } from "@mantine/modals";
import { toast } from "react-toastify";
import { MdDone } from "react-icons/md";
import { getGradeList } from "../../actions/Grade.action";
import {
  clearPaymentDue,
  getDuePayments,
} from "../../actions/Payment.action";
import { months, years } from "../../constants/MonthsAndYears";

const tableCellStyle = {
  padding: "0.5rem",
  verticalAlign: "middle",
};

const centeredTableCellStyle = {
  ...tableCellStyle,
  textAlign: "center",
};

const DueStudents = ({ grades, getGradeList }) => {
  const modals = useModals();
  const [duePayments, setDuePayments] = useState([]);
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(months[new Date().getMonth()]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (grades === null) {
      getGradeList();
    }
  }, [grades, getGradeList]);

  const currentGrade = useMemo(
    () => grades?.find((g) => g.id === parseInt(grade)),
    [grades, grade]
  );
  const currentShifts = currentGrade?.shifts || [];
  const currentBatches =
    currentGrade?.batches?.filter((bt) => bt.shiftId === parseInt(shift)) || [];

  const formatAmount = (value) => {
    const amount = parseFloat(value);
    return Number.isNaN(amount) ? "0.00" : amount.toFixed(2);
  };

  const fetchDuePayments = async () => {
    if (!year || !month) {
      toast.error("Please select year and month");
      return;
    }

    setLoading(true);
    setHasSearched(true);

    const result = await getDuePayments({
      tenant: "coaching",
      year,
      month,
      gradeId: grade,
      shiftId: shift,
      batchId: batch,
    });

    if (result.success) {
      setDuePayments(result.data || []);
    } else {
      setDuePayments([]);
      toast.error(result.error || "Error fetching due payments");
    }

    setLoading(false);
  };

  const handleClearDue = async (payment) => {
    setUpdatingId(payment.id);
    const result = await clearPaymentDue(payment.id);
    setUpdatingId(null);

    if (result.success) {
      setDuePayments((prev) => prev.filter((item) => item.id !== payment.id));
      toast.success(result.message || "Due status cleared successfully");
      return;
    }

    toast.error(result.error || "Error updating due status");
  };

  const openClearDueModal = (payment) => {
    const studentName = payment.student?.name || `Student ${payment.userId}`;

    modals.openConfirmModal({
      title: "Clear Due Status",
      centered: true,
      children: (
        <div>
          Are you sure you want to clear due for{" "}
          <strong>{studentName}</strong> for {payment.month} {payment.year}? The
          due will be cleared.
        </div>
      ),
      labels: { confirm: "Clear Due", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => handleClearDue(payment),
    });
  };

  const sortedPayments = [...duePayments].sort((a, b) => {
    const firstUid = a.student?.uid ?? a.userId;
    const secondUid = b.student?.uid ?? b.userId;
    if (firstUid < secondUid) return -1;
    if (firstUid > secondUid) return 1;
    return 0;
  });

  const totalDueAmount = sortedPayments.reduce((sum, payment) => {
    const amount = parseFloat(payment.total_amount);
    return sum + (Number.isNaN(amount) ? 0 : amount);
  }, 0);

  return (
    <Container className="pb-4">
      <Card bg="white" text="dark" className="crd shadow mb-4">
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                onChange={(e) => setYear(parseInt(e.target.value))}
                id="due-year"
                name="year"
                value={year}
              >
                <option value={""}>Select Year</option>
                {years.map((y, i) => (
                  <option key={i} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-month" className="d-block">
                  Month
                </label>
              </div>
              <Form.Select
                onChange={(e) => setMonth(e.target.value)}
                id="due-month"
                name="month"
                value={month}
              >
                <option value={""}>Select Month</option>
                {months.map((m, i) => (
                  <option key={i} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                id="due-grade"
                value={grade}
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="due-shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  id="due-shift"
                  value={shift}
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {currentShifts.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="due-batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select
                  id="due-batch"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                >
                  <option value={""}>Select Batch</option>
                  {currentBatches.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            <Col
              md={grade !== "" && shift !== "" ? 9 : 12}
              className="d-flex justify-content-end align-items-end py-3"
            >
              <Button
                onClick={fetchDuePayments}
                variant="primary"
                type="button"
                className="btn_primary"
                disabled={loading}
              >
                {loading ? "Loading..." : "Select"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="crd p-md-4 pb-md-0 p-2">
        <Card.Body>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 400 }}
            >
              <Spinner variant="dark" animation="grow" />
            </div>
          ) : sortedPayments.length === 0 ? (
            <span className="text-center fs-4 d-block pb-3">
              {hasSearched
                ? "No due students were found for the selected filters."
                : 'No due students found. Please select filters and click "Select".'}
            </span>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">
                    Due Students ({sortedPayments.length} students)
                  </h5>
                  <h6 className="text-muted mb-0">
                    {month} {year}
                  </h6>
                </div>
                <div className="text-end">
                  <span className="d-block text-muted">Total Due</span>
                  <strong>{totalDueAmount.toFixed(2)}</strong>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  size="sm"
                  style={{ fontSize: "0.875rem" }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...tableCellStyle, width: "6%" }}>Roll</th>
                      <th style={{ ...tableCellStyle, width: "16%" }}>Name</th>
                      <th style={{ ...tableCellStyle, width: "11%" }}>Phone</th>
                      <th style={{ ...tableCellStyle, width: "12%" }}>
                        Admission Fee/ Exam Fee
                      </th>
                      <th style={{ ...tableCellStyle, width: "12%" }}>
                        Session Charge/ Extra Cost
                      </th>
                      <th style={{ ...tableCellStyle, width: "10%" }}>
                        Service Charge
                      </th>
                      <th style={{ ...tableCellStyle, width: "9%" }}>Total</th>
                      <th style={{ ...tableCellStyle, width: "8%" }}>Month</th>
                      <th style={{ ...tableCellStyle, width: "7%" }}>Year</th>
                      <th
                        style={{
                          ...centeredTableCellStyle,
                          width: "6%",
                        }}
                      >
                        Due
                      </th>
                      <th
                        style={{
                          ...centeredTableCellStyle,
                          width: "10%",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPayments.map((payment) => {
                      const student = payment.student || {};
                      return (
                        <tr key={payment.id}>
                          <td
                            style={{
                              ...tableCellStyle,
                            }}
                          >
                            {student.uid || payment.userId}
                          </td>
                          <td
                            style={{
                              ...tableCellStyle,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "160px",
                            }}
                            title={student.name}
                          >
                            {student.name || `Student ${payment.userId}`}
                          </td>
                          <td
                            style={{
                              ...tableCellStyle,
                            }}
                          >
                            {student.phone || "-"}
                          </td>
                          <td style={tableCellStyle}>
                            {formatAmount(payment.exam_fee)}
                          </td>
                          <td style={tableCellStyle}>
                            {formatAmount(payment.extra_amount)}
                          </td>
                          <td style={tableCellStyle}>
                            {formatAmount(payment.amount)}
                          </td>
                          <td style={tableCellStyle}>
                            {formatAmount(payment.total_amount)}
                          </td>
                          <td style={tableCellStyle}>
                            {payment.month}
                          </td>
                          <td style={tableCellStyle}>{payment.year}</td>
                          <td
                            style={{
                              ...centeredTableCellStyle,
                            }}
                          >
                            <span
                              className="badge bg-warning text-dark"
                              style={{ fontSize: "0.7rem" }}
                            >
                              Yes
                            </span>
                          </td>
                          <td
                            style={{
                              ...centeredTableCellStyle,
                            }}
                          >
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`clear-due-${payment.id}`}>
                                  Clear due
                                </Tooltip>
                              }
                            >
                              <span className="d-inline-block">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={updatingId === payment.id}
                                  onClick={() => openClearDueModal(payment)}
                                  aria-label="Clear due"
                                  className="d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    padding: 0,
                                  }}
                                >
                                  {updatingId === payment.id ? (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      role="status"
                                    />
                                  ) : (
                                    <MdDone size={16} />
                                  )}
                                </Button>
                              </span>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

export default connect(mapStateToProps, {
  getGradeList,
})(DueStudents);
